import React, { useState } from 'react';

import { formatDate } from '@betterment/core';
import { useMutation, useQuery } from '@betterment/js-runtime';
import { Icon } from '@betterment/samba-react';
import { Box, Button, Flex, Menu, MenuButton, MenuItem, MenuList, Text } from '@betterment/style-closet-react';
import styled from '@emotion/styled';

import { DismissalModal } from './DismissalModal';
import Layout, { StyledCheckbox } from './Layout';

import { CoPilotSectionProvider, useCoPilotSectionState } from '../../contexts/CoPilotSectionContext';
import { useImpersonationModal } from '../../contexts/ImpersonationModalContext';
import { AccountLockout } from '../../services/api/types/co_pilot/account_lockouts';
import { ActionRequestParams, CoPilotResponseMeta } from '../../utils/co_pilot';

const StyleTd = styled.td`
  display: flex;
  justify-content: flex-end;
  margin-top: -0.5rem;
  margin-right: -1rem;
  max-width: 10vw;
`;

const StyledMenuList = styled(MenuList)`
  transform: translateX(-1.6rem);
`;

const MinWidthTd = styled.td`
  max-width: 10vw;
`;

const AccountLockoutsRow: React.FC<{ accountLockout: AccountLockout }> = ({ accountLockout }) => {
  const { handleSendReminder, handleIdentifiedDismissals, refresh } = useCoPilotSectionState();
  const { clientName, lockoutStatus, id, suspendedAt, uploadDocumentsPath } = accountLockout;
  const [isRowDismissed, setIsRowDismissed] = useState(false);

  const [isDismissModalOpen, setIsDismissModalOpen] = useState(false);
  const { isItemSelected, handleSelectedItemChange } = useCoPilotSectionState<AccountLockout>();

  const closeImpersonationsModalHandler = (): void => {
    refresh();
  };

  const { openModal: openImpersonationModal } = useImpersonationModal();
  const resendInstructionsFn = (localId: string): void => {
    void handleSendReminder([localId]);
  };
  const uploadDocumentsFn = (): void => openImpersonationModal(uploadDocumentsPath, closeImpersonationsModalHandler);

  const removeFromListFn = () => {
    setIsRowDismissed(true);
    setIsDismissModalOpen(false);
    handleIdentifiedDismissals([id]);
  };

  return (
    !isRowDismissed && (
      <tr>
        <td>
          <Flex alignItems="center">
            <StyledCheckbox
              name={id}
              id={id}
              role="checkbox"
              type="checkbox"
              className="checkboxInputBase"
              checked={isItemSelected(id)}
              onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => handleSelectedItemChange(target.id)}
            />
            {clientName}
          </Flex>
        </td>
        <MinWidthTd>{formatDate(suspendedAt, 'longOrdinalSlashed')}</MinWidthTd>
        <td>{lockoutStatus}</td>
        <StyleTd>
          <Menu>
            <MenuButton ml="3" p="10px" variant="tertiary" mb="8" width="fit-content">
              <Text fontSize={2} color="bmtBlue">
                Actions
                <Icon name="CaretDown" size="xxsmall" color="icon.info" css={{ ml: 'space.100' }} />
              </Text>
            </MenuButton>
            <StyledMenuList p="10px">
              <>
                {lockoutStatus === 'Documentation rejected' && (
                  <>
                    <MenuItem p="5px" onSelect={() => resendInstructionsFn(id)}>
                      <Text color="bmtBlack">Resend instructions</Text>
                    </MenuItem>
                    <MenuItem p="5px" onSelect={uploadDocumentsFn}>
                      <Text color="bmtBlack">Upload documents</Text>
                    </MenuItem>
                  </>
                )}
                {lockoutStatus === 'Client action required' && (
                  <MenuItem p="5px" onSelect={() => resendInstructionsFn(id)}>
                    <Text color="bmtBlack">Resend instructions</Text>
                  </MenuItem>
                )}
                {lockoutStatus === 'Awaiting client documentation' && (
                  <MenuItem p="5px" onSelect={uploadDocumentsFn}>
                    <Text color="bmtBlack">Upload documents</Text>
                  </MenuItem>
                )}
                <MenuItem p="5px" onSelect={() => setIsDismissModalOpen(true)}>
                  <Text color="bmtBlack">Remove from list</Text>
                </MenuItem>
              </>
            </StyledMenuList>
          </Menu>
          <DismissalModal
            isOpen={isDismissModalOpen}
            closeHandler={() => setIsDismissModalOpen(false)}
            submitHandler={removeFromListFn}
          />
        </StyleTd>
      </tr>
    )
  );
};

const AccountLockoutsTable: React.FC = () => {
  const { itemList, status, handleSelectAllChange, lastPage, loadMore } = useCoPilotSectionState<AccountLockout>();
  return (
    <table>
      <tbody>
        <tr>
          <Text as="th" fontWeight="medium" pb={6}>
            <Flex alignItems="center">
              <StyledCheckbox
                name="selectAll"
                id="selectAll"
                role="checkbox"
                type="checkbox"
                className="checkboxInputBase"
                checked={status === 'allSelected'}
                onChange={() => handleSelectAllChange()}
              />
              Client
            </Flex>
          </Text>
          <Text as="th" fontWeight="medium" pb={6}>
            Suspended date
          </Text>
          <Text as="th" fontWeight="medium" pb={6}>
            Status
          </Text>
          <Text as="th" fontWeight="medium" pb={6} textAlign="right">
            Action
          </Text>
        </tr>
        {itemList?.map(accountLockout => (
          <AccountLockoutsRow key={accountLockout.id} accountLockout={accountLockout} />
        ))}
        {status !== 'empty' && status !== 'fetchError' && !lastPage && (
          <tr>
            <td>
              <Box pb={9}>
                <Button variant="tertiary" onClick={() => loadMore()}>
                  Load more
                </Button>
              </Box>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

const AccountLockouts: React.FC = () => {
  const { refetch } = useQuery<AccountLockout[], CoPilotResponseMeta>(`/api/v1/co_pilot/account_lockouts`, {
    skip: true,
  });

  const [postDismissal] = useMutation<{}, ActionRequestParams>('/api/v1/co_pilot/account_lockouts/dismissals');
  const [postSendReminder] = useMutation<{}, ActionRequestParams>(
    '/api/v1/co_pilot/account_lockouts/resend_instructions',
    {
      method: 'post',
    }
  );

  return (
    <CoPilotSectionProvider
      fetch={refetch}
      initialOrder={[{ col: 'client_name', dir: 'desc' }]}
      postHandleDismissal={postDismissal}
      postSendReminder={postSendReminder}
    >
      <Layout
        activeTab="Onboarding lockouts"
        header="These clients are currently locked out of their accounts and may need to upload additional identification documentation to verify their accounts."
        subHeader="You can view the latest account statuses, resend document upload instructions, upload documents on behalf of your clients or remove clients from the list."
      >
        <AccountLockoutsTable />
      </Layout>
    </CoPilotSectionProvider>
  );
};

export default AccountLockouts;
